/*
 * File: OcdDispositionSelect.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 20 Jan 2022 4:37:42 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { Select, Space, Spin } from 'antd';
import { usePrevious } from 'hooks/usePrevious';
import { OCDModel } from 'models/OCDModel';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReduxState } from 'redux/rootReducer';
import { useGetDispositionsQuery } from 'redux/services/useAnimalService';
import { useDebouncedCallback } from 'use-debounce';
import { BlockLoader } from '../BlockLoader';

interface Props {
  onChange: (value: string) => void;
}

const OcdDispositionSelect = ({ onChange }: Props): JSX.Element => {
  const { selectedCompanyDataAreaId: dataAreaId } = useSelector((state: ReduxState) => state.salesOrders);
  const [offset, _setOffset] = useState(0);
  const [searchValue, _setSearchValue] = useState('');
  const [dispositions, _setDispositions] = useState<OCDModel[]>([]);

  const prevSearchValue = usePrevious(searchValue);

  /* ******************** Hooks ******************** */
  const { data, isFetching } = useGetDispositionsQuery({ offset, dispositionNameContains: searchValue, overrideSkipTake: true });

  /* ******************** Variables ******************** */
  const options = useMemo(() => dispositions.map((option) => ({ label: option.name, value: option.name })), [dispositions]);

  /* ******************** Function ******************** */
  const debounceSearch = useDebouncedCallback((value: string) => {
    _setOffset(0);
    _setDispositions([]);
    _setSearchValue(value);
  }, 500);

  const handleScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    const bottom = e.currentTarget.scrollTop + e.currentTarget.offsetHeight === e.currentTarget.scrollHeight;

    if (bottom && data?.totalCount && offset + 25 < data.totalCount && !isFetching) _setOffset((prevState) => prevState + 25);
  };

  const dropDownRender = (menu: JSX.Element): JSX.Element => {
    if (isFetching && !offset)
      return (
        <div style={{ textAlign: 'center', padding: 10 }}>
          <Spin indicator={<BlockLoader direction="loader loader--slideUp" />} spinning />
        </div>
      );

    const moreResultsJSX =
      isFetching && offset ? (
        <div style={{ padding: '0px 10px' }}>
          <Space>
            <Spin indicator={<BlockLoader direction="loader loader--slideUp" />} size="small" />
          </Space>
        </div>
      ) : null;

    return (
      <div>
        {menu}
        {moreResultsJSX}
      </div>
    );
  };

  /* ******************** Use Effects ******************** */
  useEffect(() => {
    if (data !== undefined && !isFetching) {
      if (prevSearchValue === searchValue && offset !== 0) _setDispositions((prevState) => [...prevState, ...data.data]);
      if (prevSearchValue !== searchValue || offset === 0) _setDispositions(data.data);
    }
  }, [data, isFetching, offset, prevSearchValue, searchValue]);

  return (
    <Select
      dropdownRender={dropDownRender}
      filterOption={false}
      loading={isFetching}
      onPopupScroll={handleScroll}
      onSearch={debounceSearch}
      onSelect={onChange}
      options={options}
      placeholder="Disposition"
      showSearch
    />
  );
};

export default OcdDispositionSelect;
